import { useEffect, useState } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ShieldCheck, RotateCw } from 'lucide-react';
import { toast } from 'sonner';
import { extraApi } from '@/lib/extraApi';

/**
 * OtpVerifyBlock — reusable OTP verification gate for public homepage
 * lead forms (Get Free Quote, Book Now, Solar Saathi).
 *
 * ─── Behaviour ─────────────────────────────────────────────────────
 * • If `required === false` (admin toggle OFF) → renders nothing.
 * • Else:
 *     1. Shows a "Send OTP" button that calls POST /api/public/otp/send
 *     2. On success, shows the 6-digit input + "Verify OTP" button
 *     3. On successful verify → stores otp_token, invokes onVerified(token)
 *        and locks itself into a green "Verified ✓" state
 *     4. Resend has a 30-second cooldown to avoid MSG91 spam.
 *
 * ─── Props ─────────────────────────────────────────────────────────
 * @param {boolean}  required     — admin toggle from /public/public-lead-otp-required
 * @param {string}   mobile       — 10-digit number typed in the parent form
 * @param {string}   purpose      — 'public_lead' | 'saathi' | 'book'
 * @param {(token: string | null) => void} onVerified — parent gets otp_token
 * @param {string}   [testId]     — data-testid prefix for automated tests
 *
 * When the mobile changes AFTER verification, we invalidate the token so
 * the visitor cannot swap numbers post-verify (a common abuse vector).
 */
export default function OtpVerifyBlock({ required, mobile, purpose = 'public_lead', onVerified, testId = 'otp-block' }) {
    const [step, setStep] = useState('idle');  // idle | sent | verified
    const [otp, setOtp] = useState('');
    const [busy, setBusy] = useState(false);
    const [cooldown, setCooldown] = useState(0);
    const [verifiedFor, setVerifiedFor] = useState(''); // mobile that was verified

    // Countdown tick for the resend cooldown.
    useEffect(() => {
        if (cooldown <= 0) return;
        const t = setTimeout(() => setCooldown(c => c - 1), 1000);
        return () => clearTimeout(t);
    }, [cooldown]);

    // If the user edits their mobile AFTER verification, revoke the token
    // so they must verify the new number. Prevents "verify one, submit another".
    useEffect(() => {
        if (step === 'verified' && verifiedFor && mobile !== verifiedFor) {
            setStep('idle');
            setOtp('');
            setVerifiedFor('');
            onVerified?.(null);
        }
    }, [mobile, step, verifiedFor, onVerified]);

    if (!required) return null;

    const validMobile = /^\d{10}$/.test(mobile || '');

    const sendOtp = async () => {
        if (!validMobile) {
            toast.error('कृपया 10-digit mobile number दर्ज करें');
            return;
        }
        setBusy(true);
        try {
            await extraApi.sendPublicOtp({ mobile, purpose });
            setStep('sent');
            setCooldown(30);
            toast.success('OTP भेज दी गयी — कृपया 10 minutes के अंदर enter करें');
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'OTP भेजने में error आया');
        } finally { setBusy(false); }
    };

    const verifyOtp = async () => {
        if (!/^\d{6}$/.test(otp)) {
            toast.error('OTP 6-digit होना चाहिए');
            return;
        }
        setBusy(true);
        try {
            const { otp_token } = await extraApi.verifyPublicOtp({ mobile, otp, purpose });
            setStep('verified');
            setVerifiedFor(mobile);
            onVerified?.(otp_token);
            toast.success('Mobile verified ✓');
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'गलत OTP या expire हो चुका है');
        } finally { setBusy(false); }
    };

    // Verified — show a compact confirmation card.
    if (step === 'verified') {
        return (
            <div className='col-span-2 rounded-lg border border-emerald-300 bg-emerald-50/70 dark:bg-emerald-950/25 dark:border-emerald-800 px-3 py-2 flex items-center gap-2'
                 data-testid={`${testId}-verified`}>
                <ShieldCheck className='w-4 h-4 text-emerald-600 dark:text-emerald-400' />
                <span className='text-[13px] font-medium text-emerald-800 dark:text-emerald-300'>
                    Mobile verified — आप form submit कर सकते हैं
                </span>
                <Badge variant='outline' className='ml-auto text-[10px] border-emerald-300 text-emerald-700 dark:text-emerald-300'>+91 {verifiedFor}</Badge>
            </div>
        );
    }

    // Idle / sent — show the interactive block.
    return (
        <div className='col-span-2 rounded-lg border border-amber-300 bg-amber-50/70 dark:bg-amber-950/20 dark:border-amber-800 px-3 py-3 space-y-2'
             data-testid={testId}>
            <div className='flex items-center gap-2 text-[13px] font-medium text-amber-900 dark:text-amber-200'>
                <ShieldCheck className='w-4 h-4' />
                <span>Mobile OTP verification ज़रूरी है</span>
            </div>
            {step === 'idle' && (
                <div className='flex items-center gap-2'>
                    <p className='text-[12px] text-muted-foreground flex-1'>
                        अपने +91 {validMobile ? mobile : '__________'} पर 6-digit OTP मंगवाएँ।
                    </p>
                    <Button
                        size='sm'
                        onClick={sendOtp}
                        disabled={busy || !validMobile}
                        data-testid={`${testId}-send`}
                        className='bg-amber-600 hover:bg-amber-700 text-white'
                    >
                        {busy ? 'Sending…' : 'Send OTP'}
                    </Button>
                </div>
            )}
            {step === 'sent' && (
                <div className='space-y-2'>
                    <div className='flex items-center gap-2'>
                        <Input
                            inputMode='numeric'
                            placeholder='6-digit OTP'
                            value={otp}
                            onChange={e => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                            className='max-w-[160px]'
                            data-testid={`${testId}-input`}
                        />
                        <Button
                            size='sm'
                            onClick={verifyOtp}
                            disabled={busy || otp.length !== 6}
                            data-testid={`${testId}-verify`}
                            className='bg-emerald-600 hover:bg-emerald-700 text-white'
                        >
                            {busy ? 'Verifying…' : 'Verify OTP'}
                        </Button>
                        <Button
                            size='sm'
                            variant='ghost'
                            onClick={sendOtp}
                            disabled={busy || cooldown > 0 || !validMobile}
                            data-testid={`${testId}-resend`}
                            className='text-amber-700 hover:text-amber-900'
                        >
                            <RotateCw className='w-3.5 h-3.5 mr-1' />
                            {cooldown > 0 ? `Resend in ${cooldown}s` : 'Resend'}
                        </Button>
                    </div>
                    <p className='text-[11px] text-muted-foreground'>
                        OTP आपके WhatsApp/SMS पर आएगा — 10 minutes के लिए valid।
                    </p>
                </div>
            )}
        </div>
    );
}
